import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  FaArrowLeft,
  FaShoppingCart,
  FaHeart,
  FaExpand,
  FaTimes,
  FaRulerCombined,
  FaPalette,
  FaLeaf,
  FaGlobeAmericas,
  FaTruck,
  FaBoxOpen,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";

import { FiHeart } from "react-icons/fi";
import api from "../api";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [favorite, setFavorite] = useState(false);
  const [toast, setToast] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  const BRAND_COLOR = "#46736C";
  const DARK_TEAL = "#115E59";
  const CREAM_COLOR = "#F5EFE3";
  const LIGHT_CREAM = "#FBF8F1";
  const BORDER_COLOR = "#E5DED0";

  // =========================================================
  // GET USER
  // =========================================================

  const getUser = () => {
    try {
      return JSON.parse(
        localStorage.getItem("user") || "{}"
      );
    } catch {
      return {};
    }
  };

  // =========================================================
  // TOAST
  // =========================================================

  const showToast = (message) => {
    setToast(message);

    setTimeout(() => {
      setToast("");
    }, 2000);
  };

  // =========================================================
  // FETCH PRODUCT
  // =========================================================

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        const res = await api.get(
          `/api/products/${id}/`
        );

        const data = res.data;

        setProduct(data);

        if (data.sub_images?.length > 0) {
          setSelectedImage(
            data.sub_images[0].image
          );
        } else if (data.image) {
          setSelectedImage(data.image);
        }

        if (data.sizes?.length > 0) {
          setSelectedSize(data.sizes[0]);
        } else {
          setSelectedSize(null);
        }

        if (
          typeof data.is_favorited === "boolean"
        ) {
          setFavorite(data.is_favorited);
        }
      } catch (err) {
        console.error(
          "Failed to fetch product:",
          err
        );

        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // =========================================================
  // CLOSE FULLSCREEN WITH ESC
  // =========================================================

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener(
        "keydown",
        handleKeyDown
      );

      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );

      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  // =========================================================
  // PRICE HELPERS
  // =========================================================

  const getOriginalPrice = (size) => {
    if (
      !size ||
      size.price === null ||
      size.price === undefined ||
      size.price === ""
    ) {
      return null;
    }

    const price = Number(size.price);

    return Number.isFinite(price)
      ? price
      : null;
  };

  const getDiscountPrice = (size) => {
    if (
      !size ||
      size.discount_price === null ||
      size.discount_price === undefined ||
      size.discount_price === ""
    ) {
      return null;
    }

    const discountPrice = Number(
      size.discount_price
    );

    return Number.isFinite(discountPrice)
      ? discountPrice
      : null;
  };

  const getPricing = (size) => {
    const originalPrice =
      getOriginalPrice(size);

    const discountPrice =
      getDiscountPrice(size);

    if (
      originalPrice !== null &&
      discountPrice !== null &&
      originalPrice > 0 &&
      discountPrice < originalPrice
    ) {
      const discountPercentage = Math.round(
        ((originalPrice - discountPrice) /
          originalPrice) *
        100
      );

      return {
        originalPrice,
        finalPrice: discountPrice,
        discountPercentage,
        hasDiscount: true,
      };
    }

    return {
      originalPrice,
      finalPrice: originalPrice,
      discountPercentage: 0,
      hasDiscount: false,
    };
  };

  // =========================================================
  // SIZE HELPERS
  // =========================================================

  const formatSize = (size) => {
    if (!size) {
      return "Size unavailable";
    }

    return `${size.length_cm} × ${size.width_cm} cm`;
  };

  const formatFeet = (size) => {
    if (!size) {
      return "";
    }

    return `${size.length_ft} × ${size.width_ft} ft`;
  };

  // =========================================================
  // TOGGLE FAVORITE
  // =========================================================

  const toggleFavorite = async () => {
    const user = getUser();
    const token = user?.access;

    if (!token) {
      showToast("Please login first");
      return;
    }

    if (!product?.product_id) {
      showToast("Product ID not found");
      return;
    }

    try {
      const res = await api.post(
        "/api/favorites/toggle/",
        {
          product_id: product.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const newFavoriteStatus =
        res.data?.is_favorited;

      if (
        typeof newFavoriteStatus === "boolean"
      ) {
        setFavorite(newFavoriteStatus);

        showToast(
          newFavoriteStatus
            ? "Added to favorites"
            : "Removed from favorites"
        );
      } else {
        setFavorite((prev) => !prev);
        showToast("Favorite updated");
      }
    } catch (err) {
      console.error(
        "Favorite error:",
        err
      );

      if (
        err.response?.status === 401
      ) {
        showToast("Please login again");
      } else {
        showToast(
          err.response?.data?.detail ||
          err.response?.data?.error ||
          "Could not update favorite"
        );
      }
    }
  };

  // =========================================================
  // ADD TO CART
  // =========================================================

  const addToCart = async () => {
    const user = getUser();
    const token = user?.access;

    if (!token) {
      showToast("Please login first");
      return;
    }

    if (!product?.id) {
      showToast("Product ID not found");
      return;
    }

    if (!selectedSize) {
      showToast("Please select a size");
      return;
    }

    if (product.is_sold) {
      showToast(
        "This product has been sold"
      );
      return;
    }

    try {
      setAddingToCart(true);

      const response = await api.post(
        "/api/carts/add/",
        {
          product_id: product.id,
          size_id: selectedSize.id,
          quantity: 1,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "Cart response:",
        response.data
      );

      window.dispatchEvent(
        new Event("cartUpdated")
      );

      showToast("Added to cart");
    } catch (err) {
      console.error(
        "Add to cart error:",
        err
      );

      console.error(
        "Backend response:",
        err.response?.data
      );

      if (
        err.response?.status === 401
      ) {
        showToast("Please login again");
      } else {
        showToast(
          err.response?.data?.detail ||
          err.response?.data?.error ||
          "Could not add to cart"
        );
      }
    } finally {
      setAddingToCart(false);
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: CREAM_COLOR,
        }}
        className="
          min-h-[70vh]
          w-full
          flex
          items-center
          justify-center
          px-4
        "
      >
        <div className="text-center">
          <div
            style={{
              borderTopColor: BRAND_COLOR,
            }}
            className="
              w-10
              h-10
              border-4
              border-gray-200
              rounded-full
              animate-spin
              mx-auto
              mb-4
            "
          />

          <p
            style={{
              color: BRAND_COLOR,
            }}
            className="font-medium"
          >
            Loading product...
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // PRODUCT NOT FOUND
  // =========================================================

  if (!product) {
    return (
      <div
        style={{
          backgroundColor: CREAM_COLOR,
        }}
        className="
          min-h-[70vh]
          w-full
          flex
          items-center
          justify-center
          px-5
        "
      >
        <div className="text-center">
          <p className="text-gray-700 text-lg mb-4">
            Product not found
          </p>

          <button
            onClick={() => navigate(-1)}
            style={{
              backgroundColor: BRAND_COLOR,
            }}
            className="
              text-white
              px-6
              py-3
              rounded-xl
              font-semibold
              transition
              hover:opacity-90
            "
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // =========================================================
  // SELECTED SIZE PRICING
  // =========================================================

  const pricing = getPricing(
    selectedSize
  );

  const originalPrice =
    pricing.originalPrice;

  const finalPrice =
    pricing.finalPrice;

  const discountPercentage =
    pricing.discountPercentage;

  const hasDiscount =
    pricing.hasDiscount;

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <div
      style={{
        backgroundColor: CREAM_COLOR,
      }}
      className="
        min-h-screen
        w-full
        overflow-x-hidden
      "
    >
      {/* =====================================================
          TOAST
      ===================================================== */}

      {toast && (
        <div
          style={{
            backgroundColor: DARK_TEAL,
          }}
          className="
            fixed
            top-4
            right-4
            left-4
            sm:left-auto
            sm:right-5
            text-white
            px-5
            py-3
            rounded-xl
            shadow-2xl
            z-[10000]
            text-sm
            font-medium
            text-center
            sm:text-left
          "
        >
          {toast}
        </div>
      )}

      <div
        className="
          max-w-7xl
          mx-auto
          w-full
          px-4
          sm:px-6
          lg:px-8
          py-6
          sm:py-10
          lg:py-12
        "
      >
        {/* =====================================================
            BACK BUTTON
        ===================================================== */}

        <button
          onClick={() => navigate(-1)}
          style={{
            color: BRAND_COLOR,
          }}
          className="
            mb-6
            flex
            items-center
            gap-2
            font-medium
            text-sm
            transition
            hover:opacity-70
          "
        >
          <FaArrowLeft />
          Back to products
        </button>

        {/* =====================================================
            MAIN PRODUCT AREA
        ===================================================== */}

        <div
          className="
            grid
            grid-cols-1
            lg:grid-cols-[1.05fr_0.95fr]
            gap-6
            sm:gap-8
            lg:gap-12
            items-start
            w-full
            min-w-0
          "
        >
          {/* ===================================================
              IMAGE GALLERY
          =================================================== */}

          <div className="w-full min-w-0 lg:sticky lg:top-24">
            {/* MAIN IMAGE */}

            <div
              style={{
                backgroundColor: "#EDE4D3",
              }}
              className="
                rounded-2xl
                sm:rounded-3xl
                overflow-hidden
                shadow-sm
                relative
                group
                w-full
              "
            >
              {selectedImage ? (
                <>
                  <img
                    src={selectedImage}
                    alt={product.name}
                    onClick={() =>
                      setIsFullscreen(true)
                    }
                    className="
                      w-full
                      h-[350px]
                      sm:h-[500px]
                      lg:h-[600px]
                      object-cover
                      cursor-pointer
                      transition
                      duration-500
                      group-hover:scale-[1.02]
                    "
                  />

                  <button
                    onClick={() =>
                      setIsFullscreen(true)
                    }
                    aria-label="View image fullscreen"
                    style={{
                      backgroundColor:
                        "rgba(255,255,255,0.94)",
                      color: BRAND_COLOR,
                    }}
                    className="
                      absolute
                      top-4
                      right-4
                      w-11
                      h-11
                      rounded-full
                      flex
                      items-center
                      justify-center
                      shadow-lg
                      opacity-90
                      sm:opacity-0
                      sm:group-hover:opacity-100
                      transition
                      hover:scale-105
                    "
                  >
                    <FaExpand />
                  </button>

                  {product.is_sold && (
                    <div
                      className="
                        absolute
                        top-4
                        left-4
                        bg-black/80
                        text-white
                        px-4
                        py-2
                        rounded-lg
                        text-sm
                        font-bold
                        tracking-wide
                      "
                    >
                      SOLD
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="
                    w-full
                    h-[350px]
                    sm:h-[500px]
                    flex
                    items-center
                    justify-center
                    text-gray-400
                  "
                >
                  No image available
                </div>
              )}
            </div>

            {/* THUMBNAILS */}

            {product.sub_images?.length > 0 && (
              <div
                className="
                  flex
                  gap-3
                  sm:gap-4
                  mt-4
                  overflow-x-auto
                  pb-2
                  max-w-full
                "
              >
                {product.sub_images.map(
                  (img) => (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() =>
                        setSelectedImage(
                          img.image
                        )
                      }
                      style={{
                        borderColor:
                          selectedImage ===
                            img.image
                            ? BRAND_COLOR
                            : "transparent",
                      }}
                      className="
                        w-20
                        h-20
                        sm:w-24
                        sm:h-24
                        rounded-xl
                        overflow-hidden
                        border-2
                        flex-shrink-0
                        bg-white
                        transition
                        hover:scale-[1.03]
                      "
                    >
                      <img
                        src={img.image}
                        alt={product.name}
                        className="
                          w-full
                          h-full
                          object-cover
                        "
                      />
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {/* ===================================================
              PRODUCT INFORMATION
          =================================================== */}

          <div
            style={{
              backgroundColor: LIGHT_CREAM,
            }}
            className="
              rounded-2xl
              sm:rounded-3xl
              p-4
              sm:p-7
              lg:p-9
              shadow-sm
              w-full
              min-w-0
              overflow-hidden
            "
          >
            {/* =================================================
                TITLE + FAVORITE
            ================================================= */}

            <div
              className="
                flex
                flex-col
                sm:flex-row
                sm:justify-between
                sm:items-start
                gap-4
                min-w-0
              "
            >
              <div className="min-w-0 w-full">
                <div
                  className="
                    flex
                    items-center
                    gap-2
                    flex-wrap
                    mb-3
                  "
                >
                  {product.category && (
                    <span
                      style={{
                        backgroundColor:
                          "#E8EFEA",
                        color: DARK_TEAL,
                      }}
                      className="
                        px-3
                        py-1
                        rounded-full
                        text-xs
                        font-semibold
                        max-w-full
                        break-words
                      "
                    >
                      {product.category}
                    </span>
                  )}

                  {product.is_sold && (
                    <span
                      className="
                        bg-gray-900
                        text-white
                        px-3
                        py-1
                        rounded-full
                        text-xs
                        font-bold
                        flex-shrink-0
                      "
                    >
                      SOLD
                    </span>
                  )}
                </div>

                <h1
                  className="
                    text-2xl
                    sm:text-3xl
                    lg:text-4xl
                    font-bold
                    text-gray-900
                    leading-tight
                    break-words
                    [overflow-wrap:anywhere]
                  "
                >
                  {product.name}
                </h1>

                <p
                  className="
                    mt-3
                    text-sm
                    text-gray-500
                    break-words
                  "
                >
                  Product ID:

                  <span
                    style={{
                      color: BRAND_COLOR,
                    }}
                    className="
                      ml-2
                      font-semibold
                      break-all
                    "
                  >
                    {product.product_id}
                  </span>
                </p>
              </div>

              {/* FAVORITE */}

              <button
                onClick={
                  toggleFavorite
                }
                aria-label={
                  favorite
                    ? "Remove from favorites"
                    : "Add to favorites"
                }
                style={{
                  color: favorite
                    ? "#EF4444"
                    : BRAND_COLOR,
                  borderColor: BORDER_COLOR,
                  backgroundColor:
                    CREAM_COLOR,
                }}
                className="
                  w-12
                  h-12
                  rounded-full
                  border
                  shadow-sm
                  flex
                  items-center
                  justify-center
                  hover:scale-105
                  transition
                  flex-shrink-0
                  self-start
                "
              >
                {favorite ? (
                  <FaHeart className="text-xl" />
                ) : (
                  <FiHeart className="text-xl" />
                )}
              </button>
            </div>

            {/* =================================================
                PRICE
            ================================================= */}

            <div className="mt-7 min-w-0">
              {finalPrice !== null ? (
                <div
                  className="
                    flex
                    flex-wrap
                    items-center
                    gap-3
                    min-w-0
                  "
                >
                  <span
                    style={{
                      color: BRAND_COLOR,
                    }}
                    className="
                      text-3xl
                      sm:text-4xl
                      font-bold
                    "
                  >
                    $
                    {Number(
                      finalPrice
                    ).toFixed(2)}
                  </span>

                  {hasDiscount && (
                    <>
                      <span
                        className="
                          text-lg
                          text-gray-400
                          line-through
                        "
                      >
                        $
                        {Number(
                          originalPrice
                        ).toFixed(2)}
                      </span>

                      <span
                        className="
                          bg-red-500
                          text-white
                          px-3
                          py-1.5
                          rounded-lg
                          text-sm
                          font-bold
                        "
                      >
                        {discountPercentage}% OFF
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <p
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="
                    text-3xl
                    font-bold
                  "
                >
                  Price unavailable
                </p>
              )}

              <p
                className="
                  mt-2
                  text-sm
                  text-gray-500
                  leading-6
                "
              >
                Price shown is for the selected rug size.
              </p>
            </div>

            {/* =================================================
                SIZE SELECTOR
            ================================================= */}

            <div className="mt-8 min-w-0">
              <div
                className="
                  flex
                  flex-col
                  xs:flex-row
                  sm:flex-row
                  sm:items-center
                  sm:justify-between
                  gap-2
                  mb-3
                "
              >
                <label
                  className="
                    text-sm
                    font-bold
                    text-gray-800
                  "
                >
                  Select Size
                </label>

                {selectedSize && (
                  <span
                    style={{
                      color: BRAND_COLOR,
                    }}
                    className="
                      text-xs
                      font-semibold
                    "
                  >
                    {formatFeet(
                      selectedSize
                    )}
                  </span>
                )}
              </div>

              {product.sizes?.length > 0 ? (
                <div
                  className="
                    grid
                    grid-cols-1
                    sm:grid-cols-2
                    gap-3
                    min-w-0
                  "
                >
                  {product.sizes.map(
                    (size) => {
                      const sizePricing =
                        getPricing(size);

                      const isSelected =
                        String(
                          selectedSize?.id
                        ) ===
                        String(size.id);

                      return (
                        <button
                          key={size.id}
                          type="button"
                          onClick={() =>
                            setSelectedSize(
                              size
                            )
                          }
                          style={{
                            borderColor:
                              isSelected
                                ? BRAND_COLOR
                                : BORDER_COLOR,
                            backgroundColor:
                              isSelected
                                ? "#E8EFEA"
                                : "#FFFFFF",
                          }}
                          className="
                            relative
                            w-full
                            min-w-0
                            text-left
                            p-4
                            rounded-xl
                            border-2
                            transition
                            hover:shadow-sm
                            overflow-hidden
                          "
                        >
                          {isSelected && (
                            <div
                              style={{
                                color:
                                  BRAND_COLOR,
                              }}
                              className="
                                absolute
                                top-3
                                right-3
                              "
                            >
                              <FaCheckCircle />
                            </div>
                          )}

                          <div
                            className="
                              pr-7
                              min-w-0
                            "
                          >
                            <p
                              className="
                                font-bold
                                text-gray-900
                                text-sm
                                sm:text-base
                                break-words
                              "
                            >
                              {size.length_cm} ×{" "}
                              {size.width_cm} cm
                            </p>

                            <p
                              className="
                                text-sm
                                text-gray-500
                                mt-1
                                break-words
                              "
                            >
                              {size.length_ft} ×{" "}
                              {size.width_ft} ft
                            </p>

                            <p
                              style={{
                                color:
                                  BRAND_COLOR,
                              }}
                              className="
                                mt-2
                                font-bold
                                text-sm
                                sm:text-base
                              "
                            >
                              $
                              {Number(
                                sizePricing.finalPrice ||
                                0
                              ).toFixed(2)}
                            </p>

                            {sizePricing.hasDiscount && (
                              <p
                                className="
                                  text-xs
                                  text-gray-400
                                  line-through
                                "
                              >
                                $
                                {Number(
                                  sizePricing.originalPrice
                                ).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    }
                  )}
                </div>
              ) : (
                <p className="text-gray-500">
                  No size available
                </p>
              )}
            </div>

            {/* =================================================
                QUICK PRODUCT INFO
            ================================================= */}

            <div
              className="
                mt-8
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-3
                min-w-0
              "
            >
              <div
                style={{
                  borderColor: BORDER_COLOR,
                  backgroundColor: "#FFFFFF",
                }}
                className="
                  border
                  rounded-xl
                  p-4
                  min-w-0
                  overflow-hidden
                "
              >
                <FaRulerCombined
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="mb-2"
                />

                <p
                  className="
                    text-xs
                    text-gray-500
                  "
                >
                  Selected Size
                </p>

                <p
                  className="
                    mt-1
                    text-sm
                    font-bold
                    text-gray-800
                    break-words
                  "
                >
                  {selectedSize
                    ? formatFeet(
                      selectedSize
                    )
                    : "N/A"}
                </p>
              </div>

              <div
                style={{
                  borderColor: BORDER_COLOR,
                  backgroundColor: "#FFFFFF",
                }}
                className="
                  border
                  rounded-xl
                  p-4
                  min-w-0
                  overflow-hidden
                "
              >
                <FaGlobeAmericas
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="mb-2"
                />

                <p
                  className="
                    text-xs
                    text-gray-500
                  "
                >
                  Origin
                </p>

                <p
                  className="
                    mt-1
                    text-sm
                    font-bold
                    text-gray-800
                    break-words
                  "
                >
                  Afghanistan
                </p>
              </div>
            </div>

            {/* =================================================
                ADD TO CART
            ================================================= */}

            <button
              onClick={addToCart}
              disabled={
                !selectedSize ||
                addingToCart ||
                product.is_sold
              }
              style={
                selectedSize &&
                  !product.is_sold
                  ? {
                    backgroundColor:
                      BRAND_COLOR,
                  }
                  : undefined
              }
              className="
                mt-8
                w-full
                text-white
                py-4
                rounded-xl
                font-semibold
                flex
                items-center
                justify-center
                gap-3
                transition
                hover:opacity-90
                disabled:bg-gray-400
                disabled:cursor-not-allowed
              "
            >
              <FaShoppingCart />

              {product.is_sold
                ? "Sold Out"
                : addingToCart
                  ? "Adding..."
                  : selectedSize
                    ? "Add To Cart"
                    : "Select a Size"}
            </button>

            <div
              className="
                mt-4
                flex
                items-start
                justify-center
                gap-2
                text-sm
                text-gray-500
                text-center
              "
            >
              <FaTruck
                style={{
                  color: BRAND_COLOR,
                }}
                className="mt-0.5 flex-shrink-0"
              />

              <span>
                Free international shipping
              </span>
            </div>
          </div>
        </div>

        {/* =====================================================
            RUG DETAILS
        ===================================================== */}

        <section className="mt-10 sm:mt-14 w-full min-w-0">
          <div
            style={{
              backgroundColor: LIGHT_CREAM,
              borderColor: BORDER_COLOR,
            }}
            className="
              rounded-2xl
              sm:rounded-3xl
              border
              overflow-hidden
              w-full
              min-w-0
            "
          >
            <div
              style={{
                backgroundColor: "#E8EFEA",
              }}
              className="
                px-5
                sm:px-8
                py-5
                sm:py-6
              "
            >
              <div
                className="
                  flex
                  items-start
                  gap-3
                "
              >
                <div
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="
                    text-xl
                    flex-shrink-0
                    mt-0.5
                  "
                >
                  <FaInfoCircle />
                </div>

                <div className="min-w-0">
                  <h2
                    className="
                      text-xl
                      sm:text-2xl
                      font-bold
                      text-gray-900
                      break-words
                    "
                  >
                    Rug Details
                  </h2>

                  <p
                    className="
                      text-sm
                      text-gray-600
                      mt-1
                      leading-6
                    "
                  >
                    Important information about this handmade Afghan rug.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="
                p-4
                sm:p-6
                lg:p-8
                grid
                grid-cols-1
                sm:grid-cols-2
                lg:grid-cols-3
                gap-3
                sm:gap-4
                min-w-0
              "
            >
              {/* SIZE FT */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaRulerCombined
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Size in Ft
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                  "
                >
                  {selectedSize
                    ? formatFeet(
                      selectedSize
                    )
                    : "Not available"}
                </p>
              </div>

              {/* SIZE CM */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaRulerCombined
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Size in CM
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                  "
                >
                  {selectedSize
                    ? formatSize(
                      selectedSize
                    )
                    : "Not available"}
                </p>
              </div>

              {/* MATERIAL */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaLeaf
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Material
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                    [overflow-wrap:anywhere]
                  "
                >
                  100% High Quality Natural Wool
                </p>
              </div>

              {/* DYES */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaPalette
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Dyes
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                    [overflow-wrap:anywhere]
                  "
                >
                  100% Natural Organic Vegetable Dyes
                </p>
              </div>

              {/* CONSTRUCTION */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaCheckCircle
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Construction
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                  "
                >
                  Handmade
                </p>
              </div>

              {/* ORIGIN */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaGlobeAmericas
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Origin
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                  "
                >
                  Afghanistan
                </p>
              </div>

              {/* CONDITION */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaCheckCircle
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Condition
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                  "
                >
                  Excellent Condition
                </p>
              </div>

              {/* CATEGORY */}

              <div
                className="
                  bg-white
                  rounded-xl
                  p-4
                  sm:p-5
                  border
                  border-[#E5DED0]
                  min-w-0
                  overflow-hidden
                "
              >
                <FaInfoCircle
                  style={{
                    color: BRAND_COLOR,
                  }}
                  className="text-lg mb-3"
                />

                <p
                  className="
                    text-xs
                    uppercase
                    tracking-wider
                    text-gray-500
                    font-semibold
                  "
                >
                  Category
                </p>

                <p
                  className="
                    mt-2
                    text-gray-900
                    font-bold
                    text-sm
                    sm:text-base
                    leading-6
                    break-words
                    [overflow-wrap:anywhere]
                  "
                >
                  {product.category ||
                    "Handmade Rug"}
                </p>
              </div>

              {/* BRAND */}

              {product.brand && (
                <div
                  className="
                    bg-white
                    rounded-xl
                    p-4
                    sm:p-5
                    border
                    border-[#E5DED0]
                    min-w-0
                    overflow-hidden
                  "
                >
                  <FaInfoCircle
                    style={{
                      color: BRAND_COLOR,
                    }}
                    className="text-lg mb-3"
                  />

                  <p
                    className="
                      text-xs
                      uppercase
                      tracking-wider
                      text-gray-500
                      font-semibold
                    "
                  >
                    Brand
                  </p>

                  <p
                    className="
                      mt-2
                      text-gray-900
                      font-bold
                      text-sm
                      sm:text-base
                      leading-6
                      break-words
                      [overflow-wrap:anywhere]
                    "
                  >
                    {product.brand}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* =====================================================
            WHY THIS RUG IS SPECIAL
        ===================================================== */}

        <section className="mt-8 sm:mt-12 w-full min-w-0">
          <div
            style={{
              backgroundColor: LIGHT_CREAM,
              borderColor: BORDER_COLOR,
            }}
            className="
              rounded-2xl
              sm:rounded-3xl
              border
              p-5
              sm:p-8
              w-full
              min-w-0
              overflow-hidden
            "
          >
            <h2
              className="
                text-xl
                sm:text-2xl
                font-bold
                text-gray-900
                mb-6
                break-words
              "
            >
              Why This Rug Is Special
            </h2>

            <div className="space-y-4">
              {[
                "Handmade by skilled Afghan artisans",
                "One-of-a-kind piece",
                "Traditional Afghan weaving techniques",
                "Natural wool",
                "Natural vegetable dyes",
                "Unique variations caused by handmade weaving",
              ].map((item) => (
                <div
                  key={item}
                  className="
                    flex
                    items-start
                    gap-3
                    min-w-0
                  "
                >
                  <FaCheckCircle
                    style={{
                      color: BRAND_COLOR,
                    }}
                    className="
                      mt-1
                      flex-shrink-0
                    "
                  />

                  <p
                    className="
                      text-gray-700
                      leading-6
                      text-sm
                      sm:text-base
                      break-words
                      [overflow-wrap:anywhere]
                    "
                  >
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            FREE INTERNATIONAL SHIPPING
        ===================================================== */}

        <section className="mt-8 w-full min-w-0">
          <div
            style={{
              backgroundColor: LIGHT_CREAM,
              borderColor: BORDER_COLOR,
            }}
            className="
              rounded-2xl
              sm:rounded-3xl
              border
              p-5
              sm:p-8
              w-full
              min-w-0
              overflow-hidden
            "
          >
            <div
              className="
                flex
                items-start
                gap-3
                mb-6
                min-w-0
              "
            >
              <FaTruck
                style={{
                  color: BRAND_COLOR,
                }}
                className="
                  text-xl
                  flex-shrink-0
                  mt-1
                "
              />

              <h2
                className="
                  text-lg
                  sm:text-2xl
                  font-bold
                  text-gray-900
                  leading-tight
                  break-words
                "
              >
                FREE INTERNATIONAL SHIPPING
              </h2>
            </div>

            <div className="space-y-4">
              {[
                "Shipped via DHL or UPS",
                "Tracking number provided after shipment",
                "Carefully packaged for international transit",
                "Delivery time varies by destination and customs clearance",
              ].map((item) => (
                <div
                  key={item}
                  className="
                    flex
                    items-start
                    gap-3
                    min-w-0
                  "
                >
                  <FaCheckCircle
                    style={{
                      color: BRAND_COLOR,
                    }}
                    className="
                      mt-1
                      flex-shrink-0
                    "
                  />

                  <p
                    className="
                      text-gray-700
                      leading-6
                      text-sm
                      sm:text-base
                      break-words
                      [overflow-wrap:anywhere]
                    "
                  >
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            CUSTOMS & IMPORT FEES
        ===================================================== */}

        <section className="mt-8 w-full min-w-0">
          <div
            style={{
              backgroundColor: "#FFF8E7",
              borderColor: "#E8D9B5",
            }}
            className="
              rounded-2xl
              sm:rounded-3xl
              border
              p-5
              sm:p-8
              w-full
              min-w-0
              overflow-hidden
            "
          >
            <h2
              className="
                text-lg
                sm:text-2xl
                font-bold
                text-gray-900
                mb-4
                leading-tight
                break-words
              "
            >
              CUSTOMS & IMPORT FEES
            </h2>

            <p
              className="
                text-gray-700
                leading-7
                text-sm
                sm:text-base
                break-words
                [overflow-wrap:anywhere]
              "
            >
              Free shipping covers the shipping cost charged
              by our shipping service. Any customs duties,
              import taxes, or other fees charged by the
              destination country are the buyer&apos;s responsibility.
            </p>
          </div>
        </section>

        {/* =====================================================
            FINAL PRODUCT SUMMARY
        ===================================================== */}

        <section className="mt-8 mb-8 w-full min-w-0">
          <div
            style={{
              backgroundColor: DARK_TEAL,
            }}
            className="
              rounded-2xl
              sm:rounded-3xl
              p-5
              sm:p-8
              text-white
              w-full
              min-w-0
              overflow-hidden
            "
          >
            <div
              className="
                flex
                flex-col
                lg:flex-row
                lg:items-center
                lg:justify-between
                gap-6
                min-w-0
              "
            >
              <div className="min-w-0">
                <p
                  className="
                    text-sm
                    opacity-80
                    mb-2
                  "
                >
                  Handmade Afghan Rug
                </p>

                <h3
                  className="
                    text-xl
                    sm:text-2xl
                    font-bold
                    break-words
                    [overflow-wrap:anywhere]
                  "
                >
                  {product.name}
                </h3>

                {selectedSize && (
                  <p
                    className="
                      text-sm
                      mt-2
                      opacity-80
                      break-words
                    "
                  >
                    Selected size:{" "}
                    {formatFeet(selectedSize)}
                  </p>
                )}
              </div>

              <div
                className="
                  flex
                  flex-col
                  sm:flex-row
                  items-stretch
                  sm:items-center
                  gap-3
                  w-full
                  lg:w-auto
                "
              >
                {finalPrice !== null && (
                  <div
                    className="
                      text-left
                      sm:text-right
                      mr-0
                      sm:mr-3
                    "
                  >
                    <p
                      className="
                        text-xs
                        opacity-70
                      "
                    >
                      Price
                    </p>

                    <p
                      className="
                        text-2xl
                        font-bold
                      "
                    >
                      $
                      {Number(
                        finalPrice
                      ).toFixed(2)}
                    </p>
                  </div>
                )}

                <button
                  onClick={addToCart}
                  disabled={
                    !selectedSize ||
                    addingToCart ||
                    product.is_sold
                  }
                  className="
                    w-full
                    sm:w-auto
                    px-6
                    py-3.5
                    rounded-xl
                    bg-white
                    font-bold
                    transition
                    hover:bg-gray-100
                    disabled:bg-gray-400
                    disabled:cursor-not-allowed
                  "
                  style={{
                    color: DARK_TEAL,
                  }}
                >
                  {product.is_sold
                    ? "Sold Out"
                    : addingToCart
                      ? "Adding..."
                      : "Add To Cart"}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* =======================================================
          FULLSCREEN IMAGE VIEWER
      ======================================================= */}

      {isFullscreen &&
        selectedImage && (
          <div
            className="
              fixed
              inset-0
              z-[9999]
              bg-black/95
              flex
              items-center
              justify-center
              p-4
            "
            onClick={() =>
              setIsFullscreen(false)
            }
          >
            <button
              onClick={() =>
                setIsFullscreen(false)
              }
              aria-label="Close fullscreen image"
              className="
                absolute
                top-5
                right-5
                w-12
                h-12
                rounded-full
                bg-white
                text-gray-800
                flex
                items-center
                justify-center
                shadow-xl
                hover:scale-105
                transition
                z-10
              "
            >
              <FaTimes className="text-xl" />
            </button>

            <img
              src={selectedImage}
              alt={product.name}
              onClick={(e) =>
                e.stopPropagation()
              }
              className="
                max-w-full
                max-h-[95vh]
                object-contain
                rounded-lg
                shadow-2xl
              "
            />
          </div>
        )}
    </div>
  );
}